import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Flex,
  Spinner,
  Badge,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import {
  GetAllCalculations,
  CreateCalculation,
  deleteCalculation,
} from '../api/calculation';
import CalculationModalComponent from '../components/CalculationModalComponent';
import DeleteCalculationModalComponent from '../components/DeleteCalculationModalComponent';

const CalculationList = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate(); // Initialize useNavigate
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [isCalculationModalOpen, setCalculationModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const fetchCalculations = async () => {
    const data = await GetAllCalculations();
    return data;
  };

  const {
    data: calculationsData,
    isLoading,
    error,
  } = useQuery('calculations', fetchCalculations, {
    refetchOnWindowFocus: false,
  });

  const handleAddCalculation = () => {
    setSelectedCalculation(null); // Modal sıfırlanır
    navigate(`/settings/calculation/functions/new`); // "new" ile yönlendirme
  };

  const handleSaveCalculation = async calculationData => {
    try {
      await CreateCalculation(calculationData);
      toast({
        title: 'Success',
        description: 'Calculation created successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      queryClient.invalidateQueries('calculations');
      setCalculationModalOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create calculation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteCalculation = calculation => {
    setSelectedCalculation(calculation);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCalculation(selectedCalculation.id);
      toast({
        title: 'Success',
        description: 'Calculation deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      queryClient.invalidateQueries('calculations');
      setDeleteModalOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete calculation.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEdit = calculation => {
    // Navigate to the specified URL with the calculation ID
    navigate(`/settings/calculation/functions/${calculation.id}`);
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return <div>Error fetching data</div>;
  }

  return (
    <Box w="100%" minH="70vh" display="flex" flexDirection="column" p={5}>
      <Flex justifyContent="space-between" alignItems="center" mb={5}>
        <Heading size="md">Calculation List</Heading>
      </Flex>

      <Box flex="1" overflowY="auto" maxHeight="70vh">
        <TableContainer>
          <Table variant="simple" colorScheme="gray" size="sm">
            <Thead>
              <Tr>
                <Th width="20%">Enabled</Th>
                <Th width="60%">Name</Th>
                <Th width="20%" textAlign="right">
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {calculationsData.map(calculation => (
                <Tr key={calculation.id}>
                  <Td>
                    <Badge
                      colorScheme={calculation.enabled ? 'green' : 'red'}
                      px={2}
                      py={1}
                      borderRadius="md"
                      textAlign="center"
                    >
                      {calculation.enabled ? 'ENABLED' : 'DISABLED'}
                    </Badge>
                  </Td>
                  <Td>{calculation.name}</Td>
                  <Td textAlign="right">
                    <Flex justifyContent="flex-end">
                      <Text
                        as="span"
                        mr={1}
                        cursor="pointer"
                        color="black"
                        onClick={() => handleEdit(calculation)}
                      >
                        edit
                      </Text>
                      <Text mx={2}>|</Text>
                      <Text
                        as="span"
                        cursor="pointer"
                        color="black"
                        onClick={() => handleDeleteCalculation(calculation)}
                      >
                        delete
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Action Buttons */}
      <Flex justifyContent="flex-end" alignItems="center" mt={4}>
        <Button mr={2} variant="outline" colorScheme="gray">
          Transfer
        </Button>
        <Button mr={2} variant="outline" colorScheme="gray">
          Export
        </Button>
        <Button mr={2} variant="outline" colorScheme="gray">
          Import
        </Button>
        <Button
          variant="solid"
          colorScheme="gray"
          onClick={handleAddCalculation}
        >
          Add Calculation
        </Button>
      </Flex>

      <CalculationModalComponent
        isOpen={isCalculationModalOpen}
        onClose={() => setCalculationModalOpen(false)} // Modalın kapatılmasını sağlar
        onSuccess={() => queryClient.invalidateQueries('calculations')} // Listeyi yeniden yükler
      />

      {isDeleteModalOpen && (
        <DeleteCalculationModalComponent
          isOpen={isDeleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          calculation={selectedCalculation}
          onSuccess={() => queryClient.invalidateQueries('calculations')}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
};

export default CalculationList;
